import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Input } from 'rsuite';
import { showToast } from '../../utils/toast';
import {
  useDiscardTimer,
  useSaveTimesheetEntry,
  useStartTimer,
  useStopTimer,
  useTimesheetTimer,
  useUpdateTimer,
} from '../../hooks/useTimesheet';
import { formatClock12, formatDate, formatElapsed, formatHM, isoFromDate, nowClock, shiftClock } from '../../utils/timesheet';
import { findOpenSlot, findOverlap } from './overlapFix';
import { ClockPicker, RdChip, TaskPicker, clockSpanSeconds, suggestStartTime } from './TimesheetParts';

/**
 * The log time card in My Timesheet's sidebar.
 *
 * Start and end times are the default - pick the task, add a note, pick when it
 * started and ended, and the length works itself out. A link under the form
 * switches it to a timer instead (kept on the server, so it survives a closed
 * tab and follows the person between devices); while one runs the card turns
 * into the timer, and stopping it logs the entry, rounded to the configured
 * increment.
 *
 * `date` is the day the page is on. The start time is pre-filled with when the
 * last entry that day ended.
 */
// How the timer's rounding reads in its tooltip.
const ROUNDING_WORDS = { nearest: 'to the nearest', up: 'up to', down: 'down to' };

export default function LogTimeBar({ tasks, config, date, entriesForDate }) {
  const timerQuery = useTimesheetTimer();
  const timer = timerQuery.data?.timer || null;
  const saveMutation = useSaveTimesheetEntry();
  const startMutation = useStartTimer();
  const stopMutation = useStopTimer();
  const updateMutation = useUpdateTimer();
  const discardMutation = useDiscardTimer();

  const [taskId, setTaskId] = useState(null);
  // The R&D chip: null follows the task, true/false is the person's call for
  // this entry. A new task starts over from its own answer.
  const [rdOverride, setRdOverride] = useState(null);
  useEffect(() => {
    setRdOverride(null);
  }, [taskId]);
  const [description, setDescription] = useState('');
  const [startTime, setStartTime] = useState(null);
  const [endTime, setEndTime] = useState(null);
  const [now, setNow] = useState(Date.now());
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  // Start and end times unless the person switches to the timer; a running
  // timer always shows, wherever it was started.
  const [mode, setMode] = useState('range');
  const skewRef = useRef(0);
  // Whether the start time still tracks where the day's last entry ended. It
  // does on a new day, until the person picks a start or logs an entry - after
  // which a refetch of the day must not overwrite the start they now have.
  const followSuggestion = useRef(true);

  const dayEntries = useMemo(() => entriesForDate(date), [entriesForDate, date]);
  const suggestedStart = suggestStartTime(dayEntries);

  useEffect(() => {
    followSuggestion.current = true;
    setEndTime(null);
  }, [date, timer]);

  useEffect(() => {
    if (timer || !followSuggestion.current) return;
    setStartTime(suggestedStart);
  }, [date, suggestedStart, timer]);

  // Mirror a running timer into the row, including one started elsewhere.
  useEffect(() => {
    if (!timer) return undefined;
    setTaskId(timer.taskId);
    setDescription(timer.description || '');
    skewRef.current = new Date(timer.serverNow).getTime() - Date.now();
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [timer]);

  const taskById = useMemo(() => new Map((tasks || []).map((t) => [t.id, t])), [tasks]);
  const task = taskById.get(taskId);
  const exempt = new Set(config?.descriptionExemptRoles || []);
  const needsDescription = Boolean(config?.settings?.requireDescription) && !exempt.has(task?.systemRole);
  const rdOn = rdOverride ?? Boolean(task?.effectiveRdEligible);
  const seconds = clockSpanSeconds(startTime, endTime);
  // Zero is fine: the same start and end logs a zero-length entry.
  const lengthOk = seconds !== null;
  // The entry it would overlap, caught before Add entry. With overlaps turned
  // off it can't be added until it moves - which "Automatically find a spot"
  // does, keeping its length.
  const clash = mode === 'range' && lengthOk ? findOverlap({ entries: dayEntries, startTime, endTime }) : null;
  const clashBlocks = Boolean(clash) && config?.settings?.allowOverlaps === false;
  const filledIn = Boolean(taskId && date && (!needsDescription || description.trim()));
  const canAdd = filledIn && lengthOk && !clashBlocks;

  const findSpot = () => {
    const slot = findOpenSlot({ entries: dayEntries, startTime, endTime });
    if (!slot) {
      showToast('warning', `There's no open ${formatHM(seconds)} left on this day.`);
      return;
    }
    followSuggestion.current = false;
    setStartTime(slot.startTime);
    setEndTime(slot.endTime);
    // Moved and logged in one go; with something still to fill in, it only moves.
    if (filledIn) add(slot);
    else showToast('warning', taskId ? 'Say what you worked on, then add it.' : 'Pick a task, then add it.');
  };

  const startedAtMs = timer ? new Date(timer.startedAt).getTime() : null;
  const elapsed = timer ? Math.max(0, (now + skewRef.current - startedAtMs) / 1000) : 0;

  const fail = (err) => showToast('error', String(err?.message || err));

  useEffect(() => {
    if (timer) setMode('timer');
  }, [timer]);

  // `at` logs it at other times (a spot found for it) than the ones showing.
  const add = async (at = null) => {
    if (!(at ? filledIn : canAdd)) return;
    const from = at?.startTime || startTime;
    const to = at?.endTime || endTime;
    try {
      await saveMutation.mutateAsync({
        taskId,
        date,
        startTime: from,
        endTime: to,
        durationSeconds: clockSpanSeconds(from, to),
        description: description.trim(),
        rdEligible: rdOn,
      });
      setTaskId(null);
      setDescription('');
      // The next block usually starts where this one ended.
      followSuggestion.current = false;
      setStartTime(to);
      setEndTime(null);
    } catch (err) {
      fail(err);
    }
  };

  const startTimer = async () => {
    try {
      await startMutation.mutateAsync({ taskId: taskId || null, description });
    } catch (err) {
      fail(err);
    }
  };

  // Saved as the person edits, so the server copy is what gets logged even if
  // they stop it from another tab.
  const persist = (patch) => {
    if (!timer) return;
    updateMutation.mutate(
      { taskId: patch.taskId !== undefined ? patch.taskId : taskId, description: patch.description !== undefined ? patch.description : description },
      { onError: fail }
    );
  };

  const stopTimer = async () => {
    if (!taskId) {
      showToast('warning', 'Pick a task before stopping the timer.');
      return;
    }
    if (needsDescription && !description.trim()) {
      showToast('warning', 'Say what you worked on before stopping the timer.');
      return;
    }
    const started = new Date(startedAtMs - skewRef.current);
    const ended = new Date();
    const sameDay = isoFromDate(started) === isoFromDate(ended);
    try {
      const result = await stopMutation.mutateAsync({
        taskId,
        description,
        date: isoFromDate(started),
        startTime: sameDay ? nowClock(started) : null,
        endTime: sameDay ? nowClock(ended) : null,
        rdEligible: rdOn,
      });
      showToast('success', `Logged ${formatHM(result.entry.durationSeconds)}.`);
      setTaskId(null);
      setDescription('');
      setMode('range');
    } catch (err) {
      fail(err);
    }
  };

  const discard = async () => {
    try {
      await discardMutation.mutateAsync();
      setConfirmDiscard(false);
      setTaskId(null);
      setDescription('');
      setMode('range');
    } catch (err) {
      fail(err);
    }
  };

  const rounding = `Rounded ${ROUNDING_WORDS[config?.settings?.timerRoundingMode] || ROUNDING_WORDS.nearest} ${config?.settings?.timerRoundingMinutes || 15} minutes when stopped`;
  const title = timer ? 'Timer running' : mode === 'timer' ? 'Start a timer' : 'Log time';

  return (
    <form
      className={`ts-side-card ts-logcard${timer ? ' ts-logcard--running' : ''}`}
      aria-label={title}
      onSubmit={(e) => {
        e.preventDefault();
        if (timer) stopTimer();
        else if (mode === 'timer') startTimer();
        else add();
      }}
    >
      <div className="ts-side-card__head">
        <h2 className="ts-side-card__title">{title}</h2>
        {timer ? <span className="ts-logcard__live" aria-hidden="true" /> : <span className="ts-side-card__sub">{formatDate(date, { weekday: 'short', month: 'short', day: 'numeric' })}</span>}
      </div>

      {mode === 'timer' ? (
        <span className={`ts-logcard__elapsed${timer ? '' : ' is-idle'}`} title={rounding}>{formatElapsed(elapsed)}</span>
      ) : null}

      <div className="ts-logcard__field">
        <span className="ts-logcard__label">Project / task</span>
        <TaskPicker
          tasks={tasks}
          value={taskId}
          onChange={(value) => {
            setTaskId(value);
            persist({ taskId: value });
          }}
          placeholder="Choose a task"
        />
      </div>
      {task ? (
        <div className="ts-logcard__rd">
          <RdChip on={rdOn} onToggle={() => setRdOverride(!rdOn)} />
        </div>
      ) : null}
      <div className="ts-logcard__field">
        <span className="ts-logcard__label">What did you do?</span>
        <Input
          value={description}
          onChange={setDescription}
          onBlur={() => persist({ description })}
          placeholder={needsDescription ? 'Note' : 'Note (optional)'}
          aria-label="Note"
        />
      </div>

      {mode === 'timer' ? null : (
        <div className="ts-logcard__times">
          <div className="ts-logcard__field">
            <span className="ts-logcard__label">From</span>
            <ClockPicker
              value={startTime}
              onChange={(clock) => {
                followSuggestion.current = false;
                setStartTime(clock);
              }}
              aria-label="Start time"
              fallback={shiftClock(endTime, -30)}
              busy={dayEntries}
            />
          </div>
          <div className="ts-logcard__field">
            <span className="ts-logcard__label">To</span>
            <ClockPicker value={endTime} onChange={setEndTime} aria-label="End time" fallback={shiftClock(startTime, 30)} anchor={startTime} busy={dayEntries} />
          </div>
          <div className="ts-logcard__field ts-logcard__field--length">
            <span className="ts-logcard__label">Length</span>
            <span
              className={`ts-logcard__dur${seconds === null ? ' is-empty' : ''}`}
              title={startTime && endTime && seconds === null ? 'The end time is before the start time' : undefined}
            >
              {seconds === null ? (startTime && endTime ? 'Check times' : '0h 00m') : formatHM(seconds)}
            </span>
          </div>
        </div>
      )}

      {clash ? (
        <div className={`ts-logcard__clash${clashBlocks ? ' is-blocking' : ''}`} role="alert">
          <span className="ts-logcard__clashtext">
            <i className="bi bi-exclamation-triangle" aria-hidden="true" />
            <span>
              Overlaps {taskById.get(clash.taskId)?.name || 'another entry'}
              <span className="ts-logcard__clashtime">{formatClock12(clash.startTime)} – {formatClock12(clash.endTime)}</span>
            </span>
          </span>
          <button type="button" className="bdc-linkbtn ts-logcard__findspot" onClick={findSpot}>
            <i className="bi bi-magic" aria-hidden="true" />
            Automatically find a spot and add it
          </button>
        </div>
      ) : null}

      {timer ? (
        <div className="ts-logcard__pair">
          <button type="submit" className="bdc-btn ts-logcard__stop" disabled={stopMutation.isPending}>
            <i className="bi bi-stop-fill" aria-hidden="true" />
            Stop and save
          </button>
          {confirmDiscard ? (
            <button type="button" className="bdc-btn ts-logcard__discard" onClick={discard} disabled={discardMutation.isPending}>
              Discard?
            </button>
          ) : (
            <button type="button" className="bdc-btn ts-logcard__discard" onClick={() => setConfirmDiscard(true)}>Discard</button>
          )}
        </div>
      ) : mode === 'timer' ? (
        <button type="submit" className="bdc-btn bdc-btn--primary ts-side-card__action" disabled={startMutation.isPending}>
          <i className="bi bi-play-fill" aria-hidden="true" />
          Start timer
        </button>
      ) : (
        <button type="submit" className="bdc-btn bdc-btn--primary ts-side-card__action" disabled={!canAdd || saveMutation.isPending}>
          Add entry
        </button>
      )}

      {timer ? null : (
        <button type="button" className="bdc-linkbtn ts-logcard__switch" onClick={() => setMode(mode === 'timer' ? 'range' : 'timer')}>
          <i className={`bi ${mode === 'timer' ? 'bi-clock' : 'bi-stopwatch'}`} aria-hidden="true" />
          {mode === 'timer' ? 'Enter start and end times instead' : 'Use a timer instead'}
        </button>
      )}
    </form>
  );
}
